import { IonIcon, IonPage } from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { toast } from "sonner";
import { z } from "zod";
import { FormInput } from "@/components/atoms/form-ion/input";
import { TitleIcon } from "@/components/atoms/title-icon";
import { AuthContent } from "@/components/molecules/auth/content";
import { AuthHeader } from "@/components/molecules/auth/header";
import { AuthPageHeaderTitle } from "@/components/molecules/auth/page-header-title";
import { AuthSubmitButton } from "@/components/molecules/auth/submit-button";
import { checkCircleIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { arrowPathIcon, envelopeIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { useAuthFlowStore } from "@/lib/store/auth-flow";
import { authClient } from "@/utils/auth";

export function AuthForgotPassword() {
  const { t } = useTranslation();
  const state = useAuthFlowStore();
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  const form = useForm({
    defaultValues: { email: state.email },
    onSubmit: async (values) => {
      try {
        /**
         * Multi-tenant password reset flow: X-App-Slug header is automatically sent via global
         * authClient configuration (see src/utils/auth.ts).
         *
         * Backend uses this to track which app initiated the reset and redirect users back to the
         * correct whitelabel app.
         */
        const { error } = await authClient.forgetPassword({
          email: values.value.email,
          redirectTo: Routes.AuthForgotPasswordReset(),
        });

        if (error) {
          toast.error(t("page.auth.forgot-password.errors.failed"));
          return;
        }

        // Store email and show success state
        state.email = values.value.email;
        setSentEmail(values.value.email);
        setIsEmailSent(true);
      } catch (err) {
        toast.error(t("page.auth.forgot-password.errors.failed"));
      }
    },
  });

  if (isEmailSent) {
    return (
      <IonPage>
        <AuthHeader parts={{ backButton: { defaultHref: Routes.AuthSignInPassword() } }} />
        <AuthContent>
          <TitleIcon icon={checkCircleIcon} className="text-success" />

          <AuthPageHeaderTitle
            parts={{
              title: { children: t("routes:auth.forgot-password.success.title") },
              subtitle: {
                children: t("routes:auth.forgot-password.success.subtitle", {
                  email: sentEmail,
                }),
              },
            }}
          />

          <div className="flex flex-col gap-6">
            <AuthSubmitButton onClick={() => history.replace(Routes.AuthSignInPassword())}>
              {t("routes:auth.forgot-password.success.action.backToSignIn")}
            </AuthSubmitButton>
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <AuthHeader parts={{ backButton: { defaultHref: Routes.AuthSignInPassword() } }} />
      <AuthContent>
        <TitleIcon icon={arrowPathIcon} />

        <AuthPageHeaderTitle
          parts={{
            title: { children: t("routes:auth.forgot-password.title") },
            subtitle: { children: t("routes:auth.forgot-password.subtitle") },
          }}
        />

        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.validateAllFields("blur");
            void form.handleSubmit();
          }}
          className="flex flex-col gap-6"
        >
          <form.Field
            name="email"
            validators={{
              onBlur: z.string().email(t("fields.email.errors.invalid")),
            }}
            children={(field) => (
              <FormInput
                field={field}
                type="email"
                autofocusOnIonViewDidEnter
                label={t("fields.email.label")}
                autocomplete="email"
                inputMode="email"
                placeholder={t("fields.email.placeholder")}
              >
                <IonIcon
                  slot="start"
                  icon={envelopeIcon}
                  aria-hidden="true"
                  className="m-auto pl-2"
                />
              </FormInput>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting] as const}
            children={([canSubmit, isSubmitting]) => (
              <AuthSubmitButton disabled={!canSubmit} isSubmitting={isSubmitting}>
                {t("routes:auth.forgot-password.action.primary")}
              </AuthSubmitButton>
            )}
          />
        </form>
      </AuthContent>
    </IonPage>
  );
}
